import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Heart, Trophy, Send } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { kudosValues, leaderboard, employees } from '../data';
import { useApp } from '../store';
function GiveKudos() {
  const {
    addKudos,
    requireIdentity
  } = useApp();
  const [to, setTo] = useState('');
  const [value, setValue] = useState(kudosValues[0]);
  const [message, setMessage] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!to || !message.trim()) return;
    // Browsing-only visitors are re-prompted to introduce themselves; keep the
    // draft intact so they can post it once they do.
    if (!requireIdentity()) return;
    const recipient = employees.find(emp => emp.id === to);
    addKudos({
      to: recipient?.name ?? 'A teammate',
      toAvatar: recipient?.avatar ?? 'https://i.pravatar.cc/80?img=1',
      value,
      message: message.trim()
    });
    setTo('');
    setMessage('');
    setValue(kudosValues[0]);
  };
  return <form onSubmit={submit} className="bg-white rounded-xl border border-navy-100 p-5" data-id="element-665">
      <h2 className="font-serif font-bold text-lg text-navy-900 mb-4 flex items-center gap-2" data-id="element-666">
        <Award className="w-5 h-5 text-gold-500" data-id="element-667" /> Give Kudos
      </h2>
      <div className="space-y-3" data-id="element-668">
        <select value={to} onChange={e => setTo(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:outline-none text-sm text-navy-900" data-id="element-669">
          <option value="" data-id="element-670">Who do you want to recognize?</option>
          {employees.map(emp => <option key={emp.id} value={emp.id} data-id="element-671">
              {emp.name} — {emp.title}
            </option>)}
        </select>
        <div className="flex flex-wrap gap-2" data-id="element-672">
          {kudosValues.map(v => <button type="button" key={v} onClick={() => setValue(v)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${value === v ? 'bg-gold-500 text-navy-900' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`} data-id="element-673">
              {v}
            </button>)}
        </div>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} placeholder="Share what they did…" className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:outline-none text-sm text-navy-900 resize-none" data-id="element-674" />
        <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-675">
          <Send className="w-4 h-4" data-id="element-676" /> Send Recognition
        </button>
      </div>
    </form>;
}
export function RecognitionPage() {
  const {
    kudos: feed
  } = useApp();
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  return <>
      <PageHeader title="Recognition" subtitle="Celebrate the people who live our values." data-id="element-677" />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-id="element-678">
        {/* Feed */}
        <div className="space-y-5 min-w-0" data-id="element-679">
          <GiveKudos data-id="element-680" />

          {feed.map((r, i) => <motion.div key={r.id} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.04
        }} className="bg-white rounded-xl border border-navy-100 p-5" data-id="element-681">
              <div className="flex items-center gap-3 mb-3" data-id="element-682">
                <div className="flex -space-x-2" data-id="element-683">
                  <img src={r.fromAvatar} alt="" className="w-9 h-9 rounded-full ring-2 ring-white" data-id="element-684" />
                  <img src={r.toAvatar} alt="" className="w-9 h-9 rounded-full ring-2 ring-white" data-id="element-685" />
                </div>
                <p className="text-sm text-navy-700" data-id="element-686">
                  <span className="font-bold text-navy-900" data-id="element-687">{r.from}</span>{' '}
                  recognized{' '}
                  <span className="font-bold text-navy-900" data-id="element-688">{r.to}</span>
                </p>
                <span className="ml-auto text-xs text-navy-400" data-id="element-689">{r.time}</span>
              </div>
              <span className="inline-block text-[0.65rem] font-bold uppercase tracking-wide text-gold-700 bg-gold-100 px-2.5 py-1 rounded-full mb-2" data-id="element-690">
                {r.value}
              </span>
              <p className="text-navy-800 text-sm leading-relaxed" data-id="element-691">
                {r.message}
              </p>
              <button onClick={() => setLiked(l => ({
            ...l,
            [r.id]: !l[r.id]
          }))} className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${liked[r.id] ? 'text-gold-600' : 'text-navy-400 hover:text-gold-600'}`} data-id="element-692">
                <Heart className={`w-4 h-4 ${liked[r.id] ? 'fill-gold-500 text-gold-500' : ''}`} data-id="element-693" />{' '}
                {r.likes + (liked[r.id] ? 1 : 0)}
              </button>
            </motion.div>)}
        </div>

        {/* Leaderboard */}
        <aside data-id="element-694">
          <div className="bg-white rounded-xl border border-navy-100 p-5 sticky top-20" data-id="element-695">
            <h2 className="font-serif font-bold text-lg text-navy-900 mb-4 flex items-center gap-2" data-id="element-696">
              <Trophy className="w-5 h-5 text-gold-500" data-id="element-697" /> This Quarter's
              Leaders
            </h2>
            <ol className="space-y-3" data-id="element-698">
              {leaderboard.map((p, i) => <li key={p.id} className="flex items-center gap-3" data-id="element-699">
                  <span className={`w-6 text-center font-bold text-sm ${i === 0 ? 'text-gold-500' : i === 1 ? 'text-navy-400' : i === 2 ? 'text-clay' : 'text-navy-300'}`} data-id="element-700">
                    {i + 1}
                  </span>
                  <img src={p.avatar} alt="" className="w-9 h-9 rounded-full" data-id="element-701" />
                  <div className="min-w-0 flex-1" data-id="element-702">
                    <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-703">
                      {p.name}
                    </p>
                    <p className="text-xs text-navy-400" data-id="element-704">{p.department}</p>
                  </div>
                  <span className="text-sm font-bold text-gold-600" data-id="element-705">
                    {p.kudos}
                  </span>
                </li>)}
            </ol>
          </div>
        </aside>
      </div>
    </>;
}